import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Comment, ReporterPublication, ReporterEmail, RssEntry, OutreachMessage, SimilarReporter } from '@pf/shared';
import {
  useReporter,
  useReporterComments,
  useAddReporterComment,
  useDeleteReporterComment,
  useReporterSimilar,
} from '../hooks/useReporters';
import { ImportReporterModal } from '../components/ImportReporterModal';

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]?.[0] ?? '').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRelativeDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = Date.now();
  const diff = now - d.getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}yr ago`;
}

function computeCadence(articles: RssEntry[]): { label: string; detail: string } | null {
  const dated = articles
    .map((a) => a.publishedAt ? new Date(a.publishedAt).getTime() : null)
    .filter((t): t is number => t !== null)
    .sort((a, b) => b - a);
  if (dated.length < 2) return null;

  const newest = dated[0];
  const oldest = dated[dated.length - 1];
  const spanDays = (newest - oldest) / 86400000;
  if (spanDays < 1) return null;

  const perMonth = (dated.length / spanDays) * 30;
  let label: string;
  if (perMonth >= 20) label = 'Very active (daily)';
  else if (perMonth >= 8) label = 'Active (weekly+)';
  else if (perMonth >= 2) label = 'Regular (2–8/mo)';
  else label = 'Occasional';

  const detail = `~${perMonth < 1 ? '<1' : Math.round(perMonth)} article${perMonth < 2 ? '' : 's'}/month`;
  return { label, detail };
}

function statusColor(status: string): string {
  switch (status) {
    case 'replied': return 'bg-green-100 text-green-700';
    case 'opened': return 'bg-blue-100 text-blue-700';
    case 'sent':
    case 'delivered': return 'bg-gray-100 text-gray-600';
    case 'bounced':
    case 'failed': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-500';
  }
}

// ── Email validation status helpers ─────────────────────────────────────────────

function emailStatusBadge(status: string): { label: string; className: string } {
  switch (status) {
    case 'valid':
      return { label: 'Valid', className: 'bg-green-100 text-green-700' };
    case 'invalid_domain':
      return { label: 'Invalid domain', className: 'bg-red-100 text-red-700' };
    case 'invalid_mailbox':
      return { label: 'Invalid mailbox', className: 'bg-red-100 text-red-700' };
    case 'invalid_syntax':
      return { label: 'Invalid syntax', className: 'bg-red-100 text-red-700' };
    case 'error':
      return { label: 'Error', className: 'bg-red-100 text-red-700' };
    case 'disposable':
      return { label: 'Disposable', className: 'bg-amber-100 text-amber-700' };
    case 'role_based':
      return { label: 'Role-based', className: 'bg-amber-100 text-amber-700' };
    case 'pending':
      return { label: 'Pending', className: 'bg-gray-100 text-gray-500' };
    case 'unvalidated':
      return { label: 'Unvalidated', className: 'bg-gray-100 text-gray-500' };
    default:
      return { label: status, className: 'bg-gray-100 text-gray-500' };
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Avatar({
  name,
  imageUrl,
  size = 'lg',
}: {
  name: string;
  imageUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
}) {
  const [imgError, setImgError] = useState(false);
  const sizeClass =
    size === 'lg' ? 'w-20 h-20 text-2xl' :
    size === 'md' ? 'w-12 h-12 text-base' :
    'w-9 h-9 text-sm';

  if (imageUrl && !imgError) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className={`${sizeClass} rounded-full object-cover flex-shrink-0`}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center flex-shrink-0 select-none`}
    >
      {getInitials(name)}
    </div>
  );
}

function TwitterIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function ExternalLinkIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );
}

// ── Recent Articles ───────────────────────────────────────────────────────────

function RecentArticles({ articles }: { articles: RssEntry[] }) {
  const cadence = computeCadence(articles);

  if (articles.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-3">Recent Articles</h2>
        <div className="py-6 text-center border border-dashed border-gray-200 rounded-lg">
          <svg className="mx-auto w-8 h-8 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
          <p className="text-sm text-gray-400">No articles tracked yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">Recent Articles</h2>
        {cadence && (
          <div className="text-right">
            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
              {cadence.label}
            </span>
            <p className="text-xs text-gray-400 mt-0.5">{cadence.detail}</p>
          </div>
        )}
      </div>
      <ul className="divide-y divide-gray-50">
        {articles.map((article) => (
          <li key={article.id} className="py-3 first:pt-0 last:pb-0">
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-2"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                  {article.title ?? article.url}
                </p>
                {article.contentSnippet && (
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                    {article.contentSnippet}
                  </p>
                )}
                {article.publishedAt && (
                  <p className="text-xs text-gray-400 mt-1">{formatDate(article.publishedAt)}</p>
                )}
              </div>
              <ExternalLinkIcon className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-400 flex-shrink-0 mt-0.5 transition-colors" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Outreach History ──────────────────────────────────────────────────────────

function OutreachHistory({
  history,
  contactId,
}: {
  history: OutreachMessage[];
  contactId: string;
}) {
  const sent = history.filter((m) => !['draft'].includes(m.status));
  const replies = history.filter((m) => m.replyReceivedAt);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">Outreach History</h2>
        <Link
          to={`/contacts/${contactId}`}
          className="text-xs text-blue-600 hover:underline"
        >
          View contact →
        </Link>
      </div>

      {/* Stats row */}
      <div className="flex gap-4 mb-4 pb-4 border-b border-gray-100">
        <div className="text-center">
          <p className="text-xl font-semibold text-gray-900">{sent.length}</p>
          <p className="text-xs text-gray-500">Sent</p>
        </div>
        <div className="text-center">
          <p className="text-xl font-semibold text-gray-900">
            {history.filter((m) => m.openCount > 0).length}
          </p>
          <p className="text-xs text-gray-500">Opened</p>
        </div>
        <div className="text-center">
          <p className="text-xl font-semibold text-gray-900">{replies.length}</p>
          <p className="text-xs text-gray-500">Replied</p>
        </div>
      </div>

      {history.length === 0 ? (
        <p className="text-sm text-gray-400 italic">No messages sent yet.</p>
      ) : (
        <ul className="space-y-3">
          {history.map((msg) => (
            <li key={msg.id} className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {msg.subject ?? '(No subject)'}
                  </p>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${statusColor(msg.status)}`}>
                    {msg.status}
                  </span>
                </div>
                {msg.replySubject && (
                  <p className="text-xs text-green-700 mt-0.5">
                    ↩ Replied: {msg.replySubject}
                    {msg.replyReceivedAt && ` · ${formatRelativeDate(msg.replyReceivedAt)}`}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-0.5">
                  {msg.sentAt ? formatDate(msg.sentAt) : formatDate(msg.createdAt)}
                  {msg.openCount > 0 && ` · ${msg.openCount} open${msg.openCount > 1 ? 's' : ''}`}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Similar Reporters ─────────────────────────────────────────────────────────

function SimilarReporterCard({
  reporter,
  currentBeats,
}: {
  reporter: SimilarReporter;
  currentBeats: string[];
}) {
  const sharedBeats = reporter.beats.filter((b) =>
    currentBeats.some((cb) => cb.toLowerCase() === b.toLowerCase()),
  );
  const pct = Math.round(reporter.similarityScore * 100);

  return (
    <Link
      to={`/reporters/${reporter.id}`}
      className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 transition-colors group"
    >
      <Avatar name={reporter.name} imageUrl={reporter.imageUrl} size="sm" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600 truncate">
          {reporter.name}
        </p>
        {reporter.primaryPublication && (
          <p className="text-xs text-gray-500 truncate flex items-center gap-1">
            {reporter.primaryPublication.faviconUrl && (
              <img
                src={reporter.primaryPublication.faviconUrl}
                alt=""
                className="w-3 h-3 inline flex-shrink-0"
                onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
              />
            )}
            {reporter.primaryPublication.name}
          </p>
        )}
        {sharedBeats.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {sharedBeats.slice(0, 3).map((b) => (
              <span key={b} className="text-xs px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded-full">
                {b}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 text-right">
        <span className="text-xs font-medium text-gray-400">{pct}%</span>
        <p className="text-xs text-gray-300">match</p>
      </div>
    </Link>
  );
}

function SimilarReportersSection({
  reporterId,
  currentBeats,
}: {
  reporterId: string;
  currentBeats: string[];
}) {
  const { data, isLoading } = useReporterSimilar(reporterId);
  const similar = data?.data ?? [];

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Similar Reporters</h2>
        <p className="text-sm text-gray-400">Loading...</p>
      </div>
    );
  }

  if (similar.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-base font-semibold text-gray-900 mb-1">Similar Reporters</h2>
      <p className="text-xs text-gray-400 mb-4">Reporters with similar coverage patterns, ranked by embedding similarity</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {similar.map((r) => (
          <SimilarReporterCard key={r.id} reporter={r} currentBeats={currentBeats} />
        ))}
      </div>
    </div>
  );
}

// ── Comments ──────────────────────────────────────────────────────────────────

function CommentsSection({ reporterId }: { reporterId: string }) {
  const { data, isLoading } = useReporterComments(reporterId);
  const addComment = useAddReporterComment(reporterId);
  const deleteComment = useDeleteReporterComment(reporterId);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const commentsList: Comment[] = data?.comments ?? [];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    setSubmitting(true);
    try {
      await addComment.mutateAsync(draft.trim());
      setDraft('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-base font-semibold text-gray-900 mb-4">Notes & Comments</h2>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading comments...</p>
      ) : commentsList.length === 0 ? (
        <p className="text-sm text-gray-400 mb-4">No comments yet.</p>
      ) : (
        <ul className="space-y-3 mb-4">
          {commentsList.map((c) => (
            <li key={c.id} className="flex gap-3 group">
              <div className="flex-1 bg-gray-50 rounded-md px-3 py-2">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{c.text}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {c.userName && <span className="font-medium">{c.userName} · </span>}
                  {new Date(c.createdAt).toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => deleteComment.mutate(c.id)}
                className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity self-start mt-1"
                title="Delete comment"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a note or comment..."
          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          disabled={submitting || !draft.trim()}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          Post
        </button>
      </form>
    </div>
  );
}

// ── Contact Info (emails) ───────────────────────────────────────────────────────

function ContactInfo({
  emails,
  primaryEmail,
  fallbackConfidence,
}: {
  emails: ReporterEmail[];
  primaryEmail: string | null;
  fallbackConfidence: string | null;
}) {
  // Prefer the full reporter_emails list; fall back to the scalar email.
  const hasList = emails.length > 0;

  return (
    <div className="bg-white rounded-lg shadow p-5">
      <h2 className="text-base font-semibold text-gray-900 mb-4">Contact Info</h2>

      {hasList ? (
        <div>
          <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
            {emails.length > 1 ? 'Emails' : 'Email'}
          </dt>
          <ul className="space-y-3">
            {emails.map((em) => {
              const badge = emailStatusBadge(em.validationStatus);
              const isPrimary = !!primaryEmail && em.email === primaryEmail;
              const checked = formatRelativeDate(em.validatedAt);
              return (
                <li key={em.id} className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <a
                      href={`mailto:${em.email}`}
                      className="text-sm text-blue-600 hover:underline break-all"
                    >
                      {em.email}
                    </a>
                    {isPrimary && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                        Primary
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                    >
                      {badge.label}
                    </span>
                    {em.confidence && em.confidence !== 'unverified' && (
                      <span className="text-xs text-gray-400 capitalize">
                        {em.confidence} confidence
                      </span>
                    )}
                    {checked && (
                      <span className="text-xs text-gray-400">
                        checked {checked}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : primaryEmail ? (
        <div>
          <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Email</dt>
          <dd className="mt-1 text-sm text-gray-900">
            <a href={`mailto:${primaryEmail}`} className="hover:underline text-blue-600 break-all">
              {primaryEmail}
            </a>
            {fallbackConfidence && fallbackConfidence !== 'unverified' && (
              <span className="ml-2 text-xs text-gray-400 capitalize">
                ({fallbackConfidence})
              </span>
            )}
          </dd>
        </div>
      ) : (
        <p className="text-sm text-gray-400 italic">No email on file</p>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ReporterDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useReporter(id);
  const [showImportModal, setShowImportModal] = useState(false);

  const reporter = data?.data;

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-500">Loading...</div>;
  }

  if (isError || !reporter) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600">Failed to load reporter.</p>
        <button
          onClick={() => navigate('/reporters')}
          className="mt-2 text-sm text-blue-600 hover:underline"
        >
          Back to Reporters
        </button>
      </div>
    );
  }

  const allPublications: ReporterPublication[] = reporter.publications ?? [];
  const primaryPub =
    reporter.primaryPublication ??
    (allPublications.find((p) => p.isPrimary) ?? null);
  const secondaryPubs = allPublications.filter(
    (p) => !p.isPrimary && p.id !== reporter.primaryPublication?.id,
  );
  const recentArticles: RssEntry[] = reporter.recentArticles ?? [];
  const outreachHistory: OutreachMessage[] = reporter.outreachHistory ?? [];

  // reporter.url is free-form (legacy import, merges, admin edits) and often
  // points somewhere other than the primary publication — a Substack, a
  // portfolio, a former employer. Only claim "View on <publication>" when the
  // link's host actually belongs to that publication.
  const profileHref = reporter.url
    ? /^https?:\/\//i.test(reporter.url)
      ? reporter.url
      : `https://${reporter.url}`
    : null;
  const hostOf = (raw: string): string | null => {
    try {
      return new URL(raw).hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  };
  const profileHost = profileHref ? hostOf(profileHref) : null;
  const primaryHost = primaryPub?.url ? hostOf(primaryPub.url) : null;
  const profileOnPrimary = Boolean(
    profileHost &&
      primaryHost &&
      (profileHost === primaryHost || profileHost.endsWith(`.${primaryHost}`)),
  );

  return (
    <div className="p-4 md:p-6 max-w-4xl space-y-5">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <button
          onClick={() => navigate('/reporters')}
          className="text-gray-500 hover:text-gray-900 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Reporters
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-gray-900 font-medium">{reporter.name}</span>
      </div>

      {/* Header card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start gap-4">
          <Avatar name={reporter.name} imageUrl={reporter.imageUrl} size="lg" />

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900">{reporter.name}</h1>
            {reporter.title && (
              <p className="text-sm text-gray-600 mt-0.5">{reporter.title}</p>
            )}
            {primaryPub && (
              <div className="mt-1 flex items-center gap-1">
                {primaryPub.faviconUrl && (
                  <img
                    src={primaryPub.faviconUrl}
                    alt=""
                    className="w-4 h-4 flex-shrink-0"
                    onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                  />
                )}
                <Link
                  to={`/publications/${primaryPub.id}`}
                  className="text-sm text-blue-600 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {primaryPub.name}
                </Link>
              </div>
            )}

            {/* Profile link + social links */}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {profileHref && (
                <a
                  href={profileHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                  title={
                    profileOnPrimary && primaryPub
                      ? `View profile on ${primaryPub.name}`
                      : 'View full profile'
                  }
                >
                  {profileOnPrimary && primaryPub?.faviconUrl && (
                    <img
                      src={primaryPub.faviconUrl}
                      alt=""
                      className="w-4 h-4 flex-shrink-0"
                      onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                    />
                  )}
                  {profileOnPrimary && primaryPub ? `View on ${primaryPub.name}` : 'View full profile'}
                  <ExternalLinkIcon className="w-3.5 h-3.5" />
                </a>
              )}
              {reporter.twitter && (
                <a
                  href={`https://x.com/${reporter.twitter.replace(/^@/, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-gray-700 transition-colors"
                  title="X / Twitter"
                >
                  <TwitterIcon />
                </a>
              )}
              {reporter.linkedin && (
                <a
                  href={reporter.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-600 transition-colors"
                  title="LinkedIn"
                >
                  <LinkedInIcon />
                </a>
              )}
            </div>
          </div>

          {reporter.userContactId ? (
            <button
              onClick={() => navigate(`/contacts/${reporter.userContactId}`)}
              className="bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 px-4 py-2 rounded-md text-sm font-medium flex-shrink-0 transition-colors flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              In Contacts
            </button>
          ) : (
            <button
              onClick={() => setShowImportModal(true)}
              className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium flex-shrink-0 transition-colors"
            >
              Add to Contacts
            </button>
          )}
        </div>
      </div>

      {/* Two-column layout on wider screens */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Left column — main content */}
        <div className="md:col-span-2 space-y-5">
          {/* Bio */}
          {reporter.bio && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Bio</h2>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {reporter.bio}
              </p>
            </div>
          )}

          {/* Beats & Topics */}
          {reporter.beats.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-1">Beats & Topics</h2>
              <p className="text-xs text-gray-400 mb-3">Ordered by coverage frequency</p>
              <div className="flex flex-wrap gap-2">
                {reporter.beats.map((beat, i) => {
                  const opacity = Math.max(0.4, 1 - i * 0.08);
                  return (
                    <span
                      key={beat}
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-100"
                      style={{ opacity }}
                    >
                      {beat}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent Articles */}
          <RecentArticles articles={recentArticles} />

          {/* Outreach history (only if reporter is in contacts) */}
          {reporter.userContactId && (
            <OutreachHistory
              history={outreachHistory}
              contactId={reporter.userContactId}
            />
          )}

          {/* Notes & Comments */}
          {id && <CommentsSection reporterId={id} />}
        </div>

        {/* Right column — sidebar info */}
        <div className="space-y-5">
          {/* Contact Info */}
          <ContactInfo
            emails={reporter.emails ?? []}
            primaryEmail={reporter.email}
            fallbackConfidence={reporter.emailConfidence}
          />

          {/* Publications */}
          {allPublications.length > 0 && (
            <div className="bg-white rounded-lg shadow p-5">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Publications</h2>
              <ul className="space-y-3">
                {/* Primary first */}
                {primaryPub && (
                  <li className="flex items-start gap-2.5">
                    {primaryPub.faviconUrl ? (
                      <img
                        src={primaryPub.faviconUrl}
                        alt=""
                        className="w-5 h-5 rounded flex-shrink-0 mt-0.5"
                        onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                      />
                    ) : (
                      <div className="w-5 h-5 bg-gray-200 rounded flex-shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Link
                          to={`/publications/${primaryPub.id}`}
                          className="text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline"
                        >
                          {primaryPub.name}
                        </Link>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                          Primary
                        </span>
                      </div>
                    </div>
                  </li>
                )}
                {/* Secondary */}
                {secondaryPubs.map((pub) => (
                  <li key={pub.id} className="flex items-start gap-2.5">
                    {pub.faviconUrl ? (
                      <img
                        src={pub.faviconUrl}
                        alt=""
                        className="w-5 h-5 rounded flex-shrink-0 mt-0.5"
                        onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                      />
                    ) : (
                      <div className="w-5 h-5 bg-gray-200 rounded flex-shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0">
                      <Link
                        to={`/publications/${pub.id}`}
                        className="text-sm text-gray-700 hover:text-blue-600 hover:underline"
                      >
                        {pub.name}
                      </Link>
                      <p className="text-xs text-gray-400">Contributor</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Similar Reporters — full width */}
      {id && (
        <SimilarReportersSection reporterId={id} currentBeats={reporter.beats} />
      )}

      {/* Import modal */}
      {showImportModal && reporter && (
        <ImportReporterModal
          reporter={reporter}
          onClose={() => setShowImportModal(false)}
        />
      )}
    </div>
  );
}
